/** Tools (design §7.3): on-demand buttons on a step, a grid row or the whole
    wizard. `open` and `assistant` carry the transaction action blocks
    verbatim; `print`, `action`, `goto` and `call` are the wizard's own. */

import ExprInput from './ExprInput'
import { CallBlockEditor } from './CallEditor'
import {
  ActionPicker, KeyValueEditor, ObjectPicker, StepPicker, useParamNames,
} from './pickers'
import type { ObjectKind } from './pickers'
import { useWizardDesign } from './context'
import type { WizardTool, WizardToolKind } from './types'
import { TOOL_KINDS, TOOL_LABEL, blankTool } from './types'
import { PrintFields } from './PrintFields'
import {
  AddBtn, Field, Grid2, RemoveBtn, SelectInput, TextInput, cardStyle, emptyHintStyle,
} from './ui'

function OpenFields({ tool, onChange, formNo }: {
  tool: WizardTool; onChange: (t: WizardTool) => void; formNo?: string
}) {
  const open = tool.open || { obj_name: '' }
  const objType = (String(open.obj_type || 'V').toUpperCase()) as ObjectKind
  const paramNames = useParamNames(objType, open.obj_name)
  const patch = (p: Partial<NonNullable<WizardTool['open']>>) => onChange({ ...tool, open: { ...open, ...p } })
  return (
    <>
      <Grid2>
        <Field label="Object type">
          <SelectInput value={objType} onChange={(t) => patch({ obj_type: t as NonNullable<WizardTool['open']>['obj_type'], obj_name: '', params: {} })}
            options={[
              { value: 'V', label: 'Visual' }, { value: 'D', label: 'Dashboard' }, { value: 'R', label: 'Report' },
              { value: 'G', label: 'General Process' }, { value: 'T', label: 'Transaction' },
            ]} />
        </Field>
        <Field label="Object">
          <ObjectPicker objType={objType} value={open.obj_name} onChange={(obj_name) => patch({ obj_name })} />
        </Field>
        <Field label="Opens">
          <SelectInput value={open.presentation || 'drawer'} onChange={(p) => patch({ presentation: p as 'navigate' | 'drawer' })}
            options={[{ value: 'drawer', label: 'In a drawer — the wizard stays where it is' }, { value: 'navigate', label: 'On its own screen' }]} />
        </Field>
        <Field label="Title (optional)">
          <TextInput value={open.title} onChange={(title) => patch({ title: title || undefined })} />
        </Field>
        {objType === 'T' && (
          <Field label="Mode">
            <SelectInput value={open.mode || 'add'} onChange={(mode) => patch({ mode: mode as NonNullable<WizardTool['open']>['mode'] })}
              options={[
                { value: 'add', label: 'A new record' }, { value: 'edit', label: 'The matching record' },
                { value: 'edit_or_add', label: 'The matching record, else a new one' },
              ]} />
          </Field>
        )}
      </Grid2>
      {objType === 'T' ? (
        <Field label="Which record (column → template)">
          <KeyValueEditor value={open.match} onChange={(match) => patch({ match })} formNo={formNo} keyPlaceholder="COLUMN" />
        </Field>
      ) : (
        <Field label="Criteria / prompts (by name)">
          <KeyValueEditor value={open.params as Record<string, unknown> | undefined}
            onChange={(params) => patch({ params: params as NonNullable<WizardTool['open']>['params'] })}
            keyOptions={paramNames || undefined} formNo={formNo} keyPlaceholder="NAME" />
        </Field>
      )}
    </>
  )
}

function AssistantFields({ tool, onChange, formNo }: {
  tool: WizardTool; onChange: (t: WizardTool) => void; formNo?: string
}) {
  const ctx = useWizardDesign()
  const a = tool.assistant || { mode: 'intent' as const }
  const patch = (p: Partial<NonNullable<WizardTool['assistant']>>) => onChange({ ...tool, assistant: { ...a, ...p } })
  const intent = ctx.catalog?.intents.find((i) => i.id === a.intent_id)
  return (
    <>
      <Grid2>
        <Field label="Mode">
          <SelectInput value={a.mode} onChange={(mode) => patch({ mode: mode as 'intent' | 'prompt' })}
            options={[{ value: 'intent', label: 'A defined intent (no AI)' }, { value: 'prompt', label: 'Open the chat with a prompt' }]} />
        </Field>
        {a.mode === 'intent' ? (
          <Field label="Intent">
            <SelectInput value={a.intent_id} onChange={(intent_id) => patch({ intent_id })} empty="— pick —"
              options={(ctx.catalog?.intents || []).map((i) => ({ value: i.id, label: i.title ? `${i.id} — ${i.title}` : i.id }))} />
          </Field>
        ) : (
          <Field label="Title (optional)">
            <TextInput value={a.title} onChange={(title) => patch({ title: title || undefined })} />
          </Field>
        )}
      </Grid2>
      {a.mode === 'intent' ? (
        <Field label="Parameters (by name)">
          <KeyValueEditor value={a.param_map} onChange={(param_map) => patch({ param_map })}
            keyOptions={intent?.parameters} formNo={formNo} keyPlaceholder="param" />
        </Field>
      ) : (
        <Field label="Prompt">
          <ExprInput mode="template" multiline value={a.prompt} onChange={(prompt) => patch({ prompt })} formNo={formNo} />
        </Field>
      )}
    </>
  )
}

export function ToolEditor({ tool, onChange, formNo }: {
  tool: WizardTool; onChange: (t: WizardTool) => void; formNo?: string
}) {
  const ctx = useWizardDesign()
  const patch = (p: Partial<WizardTool>) => onChange({ ...tool, ...p })
  return (
    <div>
      <Grid2>
        <Field label="Label"><TextInput value={tool.label} onChange={(label) => patch({ label })} /></Field>
        <Field label="Kind">
          <SelectInput value={tool.kind} disabled={ctx.readOnly}
            onChange={(k) => onChange({ ...blankTool(k as WizardToolKind), label: tool.label, icon: tool.icon, enabled_when: tool.enabled_when })}
            options={TOOL_KINDS.map((k) => ({ value: k, label: TOOL_LABEL[k] }))} />
        </Field>
        <Field label="Icon (Material name, optional)">
          <TextInput value={tool.icon} onChange={(icon) => patch({ icon: icon || undefined })} placeholder="lock" />
        </Field>
        <Field label="Available when (expression)">
          <ExprInput value={tool.enabled_when} onChange={(e) => patch({ enabled_when: e || undefined })} formNo={formNo}
            placeholder="not_empty({line.item_code})" />
        </Field>
      </Grid2>
      {tool.kind === 'open' && <OpenFields tool={tool} onChange={onChange} formNo={formNo} />}
      {tool.kind === 'assistant' && <AssistantFields tool={tool} onChange={onChange} formNo={formNo} />}
      {tool.kind === 'print' && (
        <PrintFields value={{ report: tool.report, delivery: tool.delivery, copies: tool.copies, printer: tool.printer }}
          onChange={(p) => onChange({ ...tool, ...p })} formNo={formNo} />
      )}
      {tool.kind === 'action' && <Field label="Action"><ActionPicker value={tool.action} onChange={(action) => patch({ action })} /></Field>}
      {tool.kind === 'goto' && <Field label="Go to"><StepPicker value={tool.goto} onChange={(goto) => patch({ goto })} /></Field>}
      {tool.kind === 'call' && (
        <CallBlockEditor block={tool.call || { wizard: '' }} onChange={(call) => patch({ call })} formNo={formNo} />
      )}
    </div>
  )
}

export default function ToolsEditor({ tools, onChange, formNo, addLabel = 'Add tool' }: {
  tools: WizardTool[] | undefined
  onChange: (t: WizardTool[] | undefined) => void
  formNo?: string
  addLabel?: string
}) {
  const ctx = useWizardDesign()
  const list = tools || []
  const set = (next: WizardTool[]) => onChange(next.length ? next : undefined)
  return (
    <div>
      {list.length === 0 && <div style={emptyHintStyle}>No tools.</div>}
      {list.map((t, i) => (
        <div key={i} style={{ ...cardStyle, padding: 8, marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <strong style={{ fontSize: 13 }}>{t.label || 'Tool'} <span style={{ fontWeight: 400, opacity: 0.7 }}>· {TOOL_LABEL[t.kind]}</span></strong>
            {!ctx.readOnly && <RemoveBtn onClick={() => set(list.filter((_, j) => j !== i))} />}
          </div>
          <ToolEditor tool={t} formNo={formNo} onChange={(nt) => set(list.map((x, j) => (j === i ? nt : x)))} />
        </div>
      ))}
      {!ctx.readOnly && <AddBtn onClick={() => set([...list, blankTool('open')])}>{addLabel}</AddBtn>}
    </div>
  )
}
